
import Vue from 'vue';
import { BInputGroup, BFormInput, BInputGroupPrepend, BFormInvalidFeedback } from 'bootstrap-vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import {
	formatNumericInput,
	formatValidationInputStyle,
	dynamicValidationMoneyString,
	rangeValidation,
	restrictToNumericInput,
} from '../../utilities/input-formatting';
import { NumericInputCharacters } from '../../enums/numeric-input-characters';
import { debounce } from '../../utilities/debounce';

export default Vue.extend({
	components: {
		BInputGroup,
		BFormInput,
		BInputGroupPrepend,
		FontAwesomeIcon,
		BFormInvalidFeedback,
	},
	inheritAttrs: false,
	model: {
		prop: 'value',
		event: 'input',
	},
	props: {
		state: {
			type: Boolean,
			required: false,
			default: null,
		},
		min: {
			type: Number,
			required: false,
			default: null,
		},
		max: {
			type: Number,
			required: false,
			default: Number.MAX_SAFE_INTEGER,
		},
		showIcon: {
			// validation input icon
			type: Boolean,
			required: false,
			default: false,
		},
		hideValidationText: {
			type: Boolean,
			required: false,
			default: false,
		},
		showValidation: {
			// shows green/red validation border and validation text
			type: Boolean,
			required: false,
			default: false,
		},
		value: {
			type: Number,
			required: false,
			default: null,
		},
		precision: {
			type: Number,
			required: false,
			default: 2,
		},
		maxLength: {
			type: Number,
			required: false,
			default: null,
		},
		minLength: {
			type: Number,
			required: false,
			default: null,
		},
		ariaLabel: {
			type: String,
			required: false,
			default: '',
		},
	},
	data() {
		return {
			displayValue: '' as string,
			focused: false as boolean,
			validationStatus: null as any,
			SEARCH_DEBOUNCE: 1000 as number,
		};
	},
	computed: {
		input: {
			get(): HTMLInputElement {
				return (this as any).$refs.input as HTMLInputElement;
			},
		},
		dynamicInputStyles(): string {
			return formatValidationInputStyle(this.showIcon, this.showValidation, this.displayValue, this.minLength);
		},
		validationString(): string {
			return dynamicValidationMoneyString(this.min, this.max, this.minLength, this.maxLength);
		},
		computedAriaLabel(): string {
			if (this.ariaLabel.length > 0) {
				return this.ariaLabel;
			}
			return this.$attrs.placeholder === '' || this.$attrs.placeholder === undefined
				? 'money type input'
				: `money type input has ${this.$attrs.placeholder} placeholder`;
		},
	},
	watch: {
		value: {
			handler(val) {
				this.displayValue = val;
				if (!this.focused) this.formatDisplayValue();
			},
			immediate: true,
		},
		displayValue: function () {
			debounce(() => {
				this.isInRange();
				this.emitValidation(this.validationStatus);
			}, this.SEARCH_DEBOUNCE)();
		},
	},
	methods: {
		removeDisplayValueFormatting(): void {
			if (this.value !== null) {
				this.displayValue = this.value.toString();
			}
			this.focused = true;
			this.$emit('focus', this.$el);
		},
		formatDisplayValue(): void {
			this.displayValue = formatNumericInput(this.value, this.precision);
			this.$emit('blur', this.value);
			this.focused = false;
		},
		emitCleanValue(newValue: number | null | ''): void {
			if (newValue === '' || newValue === null) this.$emit('input', null);
			else if (newValue.toString() !== NumericInputCharacters.negative && newValue.toString() !== NumericInputCharacters.period) {
				this.displayValue = newValue ? newValue.toString() : '';
				this.$emit('input', Number(this.displayValue));
			}
		},
		isNumber(evt: KeyboardEvent): void {
			restrictToNumericInput(evt);
		},
		focus(): void {
			this.input.focus();
		},
		selected(): void {
			this.input.select();
		},
		emitValidation(valid: boolean): void {
			this.$emit('valid', valid);
		},
		isInRange(): any {
			if (this.value !== null) {
				this.validationStatus = rangeValidation(this.min, this.max, this.minLength, this.value.toString(), this.emitValidation, this.maxLength);
			}
		},
	},
});
